import React, { useState } from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import { Add, Delete, Edit, CloudDownload } from "@mui/icons-material";
import {
  useGetLabourWorkersQuery,
  useCreateLabourWorkerMutation,
  useUpdateLabourWorkerMutation,
  useDeleteLabourWorkerMutation,
  LabourWorker,
} from "../../../redux/labourTrackerApi";
import { usePostLabourWorkersImportOdooMutation } from "../../../redux/vmsApi";
import { useGetComsBlocksQuery } from "../../../redux/comsApi";

const NAVY = "#123456";
const ROLE_DEFAULT_RATE: Record<string, number> = {
  Supervisor: 500,
  Artisan: 16,
  Assistant: 13,
};

const roleColor = (role: string) => {
  if (role === "Supervisor") return "#7b1fa2";
  if (role === "Artisan") return "#1565c0";
  return "#2e7d32";
};

type WorkerForm = {
  name: string;
  firstName: string;
  surname: string;
  nationalId: string;
  email: string;
  role: "Supervisor" | "Artisan" | "Assistant";
  jobTitle: string;
  rate: number;
  defaultBlockId: string;
  active: boolean;
};

const emptyForm = (): WorkerForm => ({
  name: "",
  firstName: "",
  surname: "",
  nationalId: "",
  email: "",
  role: "Artisan",
  jobTitle: "",
  rate: 16,
  defaultBlockId: "",
  active: true,
});

interface WorkerDialogProps {
  open: boolean;
  worker: LabourWorker | null;
  onClose: () => void;
  onSave: (form: WorkerForm) => Promise<void>;
}

const WorkerDialog: React.FC<WorkerDialogProps> = ({
  open,
  worker,
  onClose,
  onSave,
}) => {
  const { data: blocks = [] } = useGetComsBlocksQuery();
  const [form, setForm] = useState<WorkerForm>(() =>
    worker
      ? {
          name: worker.name,
          firstName: worker.firstName ?? "",
          surname: worker.surname ?? "",
          nationalId: worker.nationalId ?? "",
          email: worker.email ?? "",
          role: worker.role,
          jobTitle: worker.jobTitle ?? "",
          rate: worker.rate,
          defaultBlockId: worker.defaultBlockId ?? "",
          active: worker.active,
        }
      : emptyForm(),
  );

  React.useEffect(() => {
    setForm(
      worker
        ? {
            name: worker.name,
            firstName: worker.firstName ?? "",
            surname: worker.surname ?? "",
            nationalId: worker.nationalId ?? "",
            email: worker.email ?? "",
            role: worker.role,
            jobTitle: worker.jobTitle ?? "",
            rate: worker.rate,
            defaultBlockId: worker.defaultBlockId ?? "",
            active: worker.active,
          }
        : emptyForm(),
    );
  }, [worker, open]);

  const set = (k: keyof WorkerForm, v: any) =>
    setForm((f) => ({ ...f, [k]: v }));

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{worker ? "Edit Worker" : "Add Worker"}</DialogTitle>
      <DialogContent>
        <Box display="flex" flexDirection="column" gap={2} mt={1}>
          <TextField
            size="small"
            label="Display name *"
            value={form.name}
            onChange={(e) => set("name", e.target.value)}
            fullWidth
          />
          <Box display="flex" gap={2}>
            <TextField
              size="small"
              label="First name"
              value={form.firstName}
              onChange={(e) => set("firstName", e.target.value)}
              fullWidth
            />
            <TextField
              size="small"
              label="Surname"
              value={form.surname}
              onChange={(e) => set("surname", e.target.value)}
              fullWidth
            />
          </Box>
          <TextField
            size="small"
            label="ID number"
            value={form.nationalId}
            onChange={(e) => set("nationalId", e.target.value)}
            placeholder="e.g. 63-1421882 G63"
            fullWidth
          />
          <TextField
            size="small"
            label="Job title"
            value={form.jobTitle}
            onChange={(e) => set("jobTitle", e.target.value)}
            placeholder="e.g. Bricklayer, General Worker"
            helperText="Imported automatically from Odoo — edit if needed."
            fullWidth
          />
          <FormControl size="small" fullWidth>
            <InputLabel>Role *</InputLabel>
            <Select
              value={form.role}
              label="Role *"
              onChange={(e) => {
                const r = e.target.value as WorkerForm["role"];
                set("role", r);
                set("rate", ROLE_DEFAULT_RATE[r]);
              }}
            >
              <MenuItem value="Supervisor">Supervisor</MenuItem>
              <MenuItem value="Artisan">Artisan</MenuItem>
              <MenuItem value="Assistant">Assistant</MenuItem>
            </Select>
          </FormControl>
          <TextField
            size="small"
            type="number"
            inputProps={{ step: 0.01, min: 0 }}
            label={
              form.role === "Supervisor" ? "Monthly pay ($)" : "Daily rate ($)"
            }
            helperText={
              form.role === "Supervisor"
                ? "Paid flat per pay cycle (17th → 16th), regardless of attendance."
                : "Paid per day present."
            }
            value={form.rate}
            onChange={(e) => set("rate", parseFloat(e.target.value) || 0)}
            fullWidth
          />
          <FormControl size="small" fullWidth>
            <InputLabel>Default block</InputLabel>
            <Select
              value={form.defaultBlockId}
              label="Default block"
              onChange={(e) => set("defaultBlockId", e.target.value)}
            >
              <MenuItem value="">— None —</MenuItem>
              {blocks.map((b) => (
                <MenuItem key={b.id} value={b.id}>
                  {b.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <TextField
            size="small"
            type="email"
            label="Email (optional)"
            value={form.email}
            onChange={(e) => set("email", e.target.value)}
            fullWidth
          />
          <FormControlLabel
            control={
              <Switch
                checked={form.active}
                onChange={(e) => set("active", e.target.checked)}
              />
            }
            label="Active"
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          variant="contained"
          disabled={!form.name.trim()}
          onClick={() => onSave(form)}
          sx={{ bgcolor: NAVY, "&:hover": { bgcolor: "#1a4a7a" } }}
        >
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export const LabourWorkers: React.FC = () => {
  const {
    data: workers = [],
    isLoading,
    refetch: refetchWorkers,
  } = useGetLabourWorkersQuery();
  const { data: blocks = [] } = useGetComsBlocksQuery();
  const [createWorker] = useCreateLabourWorkerMutation();
  const [updateWorker] = useUpdateLabourWorkerMutation();
  const [deleteWorker] = useDeleteLabourWorkerMutation();

  const [importFromOdoo, { isLoading: isImporting }] =
    usePostLabourWorkersImportOdooMutation();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editWorker, setEditWorker] = useState<LabourWorker | null>(null);

  const handleImportOdoo = async () => {
    if (
      !window.confirm(
        "Import active employees from Odoo? Existing workers will be skipped.",
      )
    )
      return;
    try {
      const result = await importFromOdoo().unwrap();
      const r = result as {
        imported: number;
        skipped: number;
        errors: string[];
      };
      refetchWorkers();
      window.alert(
        `Import complete: ${r.imported} imported, ${r.skipped} skipped${
          r.errors?.length ? `, ${r.errors.length} errors` : ""
        }.`,
      );
    } catch (err: any) {
      window.alert(
        `Import failed: ${
          err?.data?.error?.message ?? err?.message ?? "Unknown error"
        }`,
      );
    }
  };

  const blockMap = new Map(blocks.map((b) => [b.id!, b.name]));

  const roleRank = (role: string) => {
    if (role === "Supervisor") return 0;
    if (role === "Artisan") return 1;
    return 2;
  };

  // Group workers by their default block
  const groups: {
    blockId: string | null;
    blockName: string;
    rows: LabourWorker[];
  }[] = [];
  const byBlock = new Map<string | null, LabourWorker[]>();
  workers.forEach((w) => {
    const k = w.defaultBlockId ?? null;
    if (!byBlock.has(k)) byBlock.set(k, []);
    byBlock.get(k)!.push(w);
  });
  byBlock.forEach((rows, k) => {
    rows.sort(
      (a, b) =>
        roleRank(a.role) - roleRank(b.role) ||
        (a.displayOrder ?? 9999) - (b.displayOrder ?? 9999),
    );
    groups.push({
      blockId: k,
      blockName: k ? blockMap.get(k) ?? "Unknown block" : "Unassigned",
      rows,
    });
  });
  groups.sort((a, b) =>
    a.blockId === null
      ? 1
      : b.blockId === null
      ? -1
      : a.blockName.localeCompare(b.blockName),
  );

  const handleSave = async (form: ReturnType<typeof emptyForm>) => {
    const payload = {
      name: form.name,
      firstName: form.firstName || undefined,
      surname: form.surname || undefined,
      nationalId: form.nationalId || undefined,
      email: form.email || undefined,
      role: form.role,
      jobTitle: form.jobTitle || undefined,
      rate: form.rate,
      defaultBlockId: form.defaultBlockId || undefined,
      active: form.active,
    };
    if (editWorker?.id) {
      await updateWorker({ id: editWorker.id, data: payload });
    } else {
      await createWorker(payload);
    }
    setDialogOpen(false);
    setEditWorker(null);
  };

  const handleDelete = async (w: LabourWorker) => {
    if (
      !window.confirm(
        `Delete worker "${w.name}"? Their attendance records will also be removed.`,
      )
    )
      return;
    await deleteWorker(w.id!);
  };

  if (isLoading)
    return (
      <Box display="flex" justifyContent="center" py={8}>
        <CircularProgress />
      </Box>
    );

  return (
    <Box>
      <Box
        display="flex"
        alignItems="flex-start"
        justifyContent="space-between"
        mb={3}
      >
        <Box>
          <Typography variant="h4" fontWeight={700}>
            Workers
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {workers.length} on the books
          </Typography>
        </Box>
        <Box display="flex" gap={1}>
          <Button
            variant="outlined"
            startIcon={
              isImporting ? <CircularProgress size={16} /> : <CloudDownload />
            }
            onClick={handleImportOdoo}
            disabled={isImporting}
            sx={{ borderColor: NAVY, color: NAVY }}
          >
            Import from Odoo
          </Button>
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={() => {
              setEditWorker(null);
              setDialogOpen(true);
            }}
            sx={{ bgcolor: NAVY, "&:hover": { bgcolor: "#1a4a7a" } }}
          >
            Add Worker
          </Button>
        </Box>
      </Box>

      {groups.length === 0 ? (
        <Card>
          <CardContent>
            <Typography color="text.secondary" textAlign="center">
              No workers yet.
            </Typography>
          </CardContent>
        </Card>
      ) : (
        groups.map(({ blockId, blockName, rows }) => (
          <Card key={blockId ?? "__none"} sx={{ mb: 2 }}>
            <Box
              px={3}
              py={1.5}
              bgcolor="#f5f7fa"
              borderBottom="1px solid #e8eaed"
            >
              <Typography
                variant="caption"
                fontWeight={700}
                color={NAVY}
                letterSpacing={0.8}
                textTransform="uppercase"
              >
                {blockName} — {rows.length}{" "}
                {rows.length === 1 ? "worker" : "workers"}
              </Typography>
            </Box>
            <CardContent sx={{ p: 0 }}>
              {rows.map((w, i) => (
                <Box
                  key={w.id}
                  display="flex"
                  alignItems="center"
                  gap={2}
                  px={3}
                  py={1.5}
                  sx={{
                    borderBottom:
                      i < rows.length - 1 ? "1px solid #f5f5f5" : "none",
                  }}
                >
                  <Box flex={1} minWidth={0}>
                    <Box
                      display="flex"
                      alignItems="center"
                      gap={1}
                      flexWrap="wrap"
                    >
                      <Typography fontWeight={500}>{w.name}</Typography>
                      <Chip
                        label={w.role}
                        size="small"
                        sx={{
                          bgcolor: `${roleColor(w.role)}18`,
                          color: roleColor(w.role),
                          fontWeight: 600,
                          fontSize: 11,
                        }}
                      />
                      {w.jobTitle && (
                        <Chip
                          label={w.jobTitle}
                          size="small"
                          variant="outlined"
                          sx={{
                            borderColor: `${roleColor(w.role)}60`,
                            color: roleColor(w.role),
                            fontSize: 11,
                          }}
                        />
                      )}
                      {!w.active && (
                        <Chip
                          label="Inactive"
                          size="small"
                          sx={{
                            bgcolor: "#ffebee",
                            color: "#c62828",
                            fontSize: 11,
                          }}
                        />
                      )}
                    </Box>
                    {(w.nationalId || w.email) && (
                      <Typography variant="caption" color="text.secondary">
                        {[w.nationalId, w.email].filter(Boolean).join(" · ")}
                      </Typography>
                    )}
                  </Box>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ whiteSpace: "nowrap" }}
                  >
                    ${w.rate}
                    {w.role === "Supervisor" ? "/cycle" : "/day"}
                  </Typography>
                  <Box>
                    <IconButton
                      size="small"
                      onClick={() => {
                        setEditWorker(w);
                        setDialogOpen(true);
                      }}
                      sx={{ color: NAVY }}
                    >
                      <Edit fontSize="small" />
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={() => handleDelete(w)}
                      sx={{ color: "#c62828" }}
                    >
                      <Delete fontSize="small" />
                    </IconButton>
                  </Box>
                </Box>
              ))}
            </CardContent>
          </Card>
        ))
      )}

      <WorkerDialog
        open={dialogOpen}
        worker={editWorker}
        onClose={() => {
          setDialogOpen(false);
          setEditWorker(null);
        }}
        onSave={handleSave}
      />
    </Box>
  );
};
